import React, { useEffect, useState, useRef } from 'react';
import { ConfigProvider } from 'antd';
import { faUsers, faBox, faTachometerAlt, faStar } from '@fortawesome/free-solid-svg-icons';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../redux/store';
import { fetchStaff } from '../../../redux/MerchantsUser';
import { fetchCustomizations } from '../../../redux/Customizations';
import { fetchItemsCount, fetchSoldOutCount } from '../../../redux/Items';
import {
  fetchStaffManagementReport,
  fetchDailyOperationsReport,
  fetchCustomerFeedbackReport,
} from '../../../redux/ManagerReports';
import { useTheme } from '../../../context/ThemeContext';
import { getAntdThemeConfig } from '../../../utils/antdTheme';
import { ThemeProvider } from 'styled-components';
import MetricCard from '../../../commonComponents/MetricCard/MetricCard';
import MetricCardSkeleton from './MetricCardSkeleton';
import GreetingSection from './GreetingSection';
import AlertsSection from './AlertsSection';
import StaffSection from './StaffSection';
import ItemsSection from './ItemsSection';
import DailyOperationsSection from './DailyOperationsSection';
import CustomizationsSection from './CustomizationsSection';
import { Container, HeaderSection, MetricsGrid, DashboardTitle } from './styles';
import { METRIC_TYPES, SECTION_TITLES } from './constants';
import { AlertItem } from './types';


const DashboardManager: React.FC = () => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const [activeCollapse, setActiveCollapse] = useState<string | null>(
    METRIC_TYPES.STAFF
  );

  const dispatch = useDispatch<AppDispatch>();
  const hasFetchedRef = useRef(false);

  const { staff, loading: merchantsLoading } = useSelector(
    (state: RootState) => state.merchants
  );
  const { customizations, loading: customizationsLoading, lastUpdated } = useSelector(
    (state: RootState) => state.customizations
  );
  const { total: itemsTotal, soldOutCount, loading: itemsLoading } = useSelector(
    (state: RootState) => state.items
  );
  const {
    staffManagement,
    dailyOperations,
    customerFeedback,
    loading: managerReportsLoading,
  } = useSelector((state: RootState) => state.managerReports);

  const totalStaff = Array.isArray(staff) ? staff.length : 0;
  const totalItems = itemsTotal || 0;

  const isLoading: boolean = Boolean(
    merchantsLoading.staff ||
    customizationsLoading ||
    itemsLoading ||
    managerReportsLoading.staffManagement ||
    managerReportsLoading.dailyOperations ||
    managerReportsLoading.customerFeedback
  );

  useEffect(() => {
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      dispatch(fetchStaff());
      dispatch(fetchCustomizations({ pageNum: 1, search: '' }));
      dispatch(fetchItemsCount());
      dispatch(fetchSoldOutCount());
      dispatch(fetchStaffManagementReport());
      dispatch(fetchDailyOperationsReport());
      dispatch(fetchCustomerFeedbackReport());
    }
  }, [dispatch]);

  const handleTileClick = (tileName: string) => {
    setActiveCollapse(tileName === activeCollapse ? null : tileName);
  };

  const alerts: AlertItem[] = [];

  if (soldOutCount > 0) {
    alerts.push({
      id: 'sold-out-items',
      title: 'Sold Out Items',
      message: `You have ${soldOutCount} item${soldOutCount > 1 ? 's' : ''} marked as sold out`,
      severity: 'warning',
      count: soldOutCount,
      action: {
        label: 'View',
        route: '/items?filter=sold_out',
      },
    });
  }

  if (dailyOperations && dailyOperations.pendingOrders > 5) {
    alerts.push({
      id: 'pending-orders',
      title: 'Pending Orders',
      message: `You have ${dailyOperations.pendingOrders} orders still pending`,
      severity: 'warning',
      count: dailyOperations.pendingOrders,
      action: {
        label: 'View',
        route: '/orders?filter=pending',
      },
    });
  }

  if (customerFeedback && customerFeedback.averageRating < 3.5) {
    alerts.push({
      id: 'low-rating',
      title: 'Low Customer Rating',
      message: `Average rating is ${customerFeedback.averageRating.toFixed(1)} — below target of 3.5`,
      severity: 'error',
      action: {
        label: 'View Feedback',
        route: '/sales-analytics',
      },
    });
  }

  return (
    <ThemeProvider theme={{ mode: isDarkMode ? 'dark' : 'light' }}>
      <ConfigProvider theme={getAntdThemeConfig(isDarkMode)}>
        <Container>
          <HeaderSection>
            <DashboardTitle level={2} style={{ margin: 0 }}>
              Dashboard
            </DashboardTitle>
          </HeaderSection>
          <GreetingSection lastUpdated={lastUpdated || 'Never'} />
          <MetricsGrid>
            {isLoading ? (
              <>
                <MetricCardSkeleton />
                <MetricCardSkeleton />
                <MetricCardSkeleton />
                <MetricCardSkeleton />
              </>
            ) : (
              <>
                <MetricCard
                  title={SECTION_TITLES.STAFF}
                  value={staffManagement?.totalStaff ?? totalStaff}
                  icon={faUsers}
                  iconColor="#2D6CCA"
                  onClick={() => handleTileClick(METRIC_TYPES.STAFF)}
                  isSelected={activeCollapse === METRIC_TYPES.STAFF}
                />
                <MetricCard
                  title={SECTION_TITLES.DAILY_OPERATIONS}
                  value={dailyOperations?.totalOrders ?? 0}
                  icon={faTachometerAlt}
                  iconColor="#4299E1"
                  onClick={() => handleTileClick(METRIC_TYPES.DAILY_OPERATIONS)}
                  isSelected={activeCollapse === METRIC_TYPES.DAILY_OPERATIONS}
                />
                <MetricCard
                  title="Customer Rating"
                  value={customerFeedback?.averageRating ? `${customerFeedback.averageRating.toFixed(1)} / 5` : '—'}
                  icon={faStar}
                  iconColor="#9F7AEA"
                />
                <MetricCard
                  title={SECTION_TITLES.ITEMS}
                  value={totalItems}
                  icon={faBox}
                  iconColor="#48BB78"
                  onClick={() => handleTileClick(METRIC_TYPES.ITEMS)}
                  isSelected={activeCollapse === METRIC_TYPES.ITEMS}
                />
              </>
            )}
          </MetricsGrid>

          <AlertsSection alerts={alerts} isLoading={isLoading} />

          <StaffSection
            isOpen={activeCollapse === METRIC_TYPES.STAFF}
            isLoading={isLoading}
            staffData={staff}
            activeStaff={staffManagement?.activeStaff}
            inactiveStaff={staffManagement?.inactiveStaff}
            recentHires={staffManagement?.recentHires}
          />
          <DailyOperationsSection
            isOpen={activeCollapse === METRIC_TYPES.DAILY_OPERATIONS}
            onClose={() => handleTileClick(METRIC_TYPES.DAILY_OPERATIONS)}
            isLoading={isLoading}
            dailyOperations={dailyOperations}
          />
          <ItemsSection
            isOpen={activeCollapse === METRIC_TYPES.ITEMS}
            onClose={() => handleTileClick(METRIC_TYPES.ITEMS)}
            isLoading={isLoading}
            itemsCount={totalItems}
            soldOutCount={soldOutCount}
          />
          <CustomizationsSection
            isOpen={activeCollapse === METRIC_TYPES.CUSTOMIZATIONS}
            onClose={() => handleTileClick(METRIC_TYPES.CUSTOMIZATIONS)}
            isLoading={isLoading}
            customizationsData={customizations}
          />
        </Container>
      </ConfigProvider>
    </ThemeProvider>
  );
};

export default DashboardManager;
